import LiveMetricController from "../../report/live_metric_controller";

// The tenant board's "Add live metric" dialog. Stages an unsaved card through the add-card controller
// on the surrounding edit grid, or navigates to the edit page with the metric staged from :show.
export default class extends LiveMetricController {
  // Stimulus merges a subclass's statics with its parent's, so this declares only what the base does not:
  // the show page opens the dialog with a URL template instead of staging in place.
  static values = { showUrlTemplate: String };

  connect() {
    // A name the user never touched keeps tracking the selection; once they type, it is theirs.
    this.nameIsGenerated = true;
    this.syncAll();
    // The dialog is opened by a sibling controller, so there is no open event to hook: observe the
    // hidden class the dialog toggles and focus the first control when it comes off.
    this.observeOpen();
  }

  disconnect() {
    super.disconnect();
    this.openObserver?.disconnect();
  }

  observeOpen() {
    this.openObserver = new MutationObserver(() => {
      const open = !this.element.classList.contains("hidden");
      if (open && !this.wasOpen) {
        // Reused across opens, so reset everything a previous Add left behind.
        this.staged = false;
        this.nameIsGenerated = true;
        this.syncName();
        this.focusFirstControl();
      }
      this.wasOpen = open;
    });
    this.wasOpen = !this.element.classList.contains("hidden");
    this.openObserver.observe(this.element, { attributes: true, attributeFilter: ["class"] });
  }

  // The metric select is a button-triggered combobox, so focus goes to its trigger rather than an input.
  focusFirstControl() {
    this.element.querySelector("[data-select-target='trigger']")?.focus();
  }

  // Enter anywhere in the dialog commits, except from the metric combobox — that control uses Enter to
  // pick the highlighted option, and stealing it would make the list unusable by keyboard.
  submitOnEnter(event) {
    if (event.target.closest("[data-controller~='select']")) return;
    // Bound at the dialog container, so this also sees Enter on Cancel and the X. Preventing their native
    // activation would stage a card where the user asked to close.
    if (event.target.closest("button")) return;

    event.preventDefault();
    // Blurred first so the field's own focusout sanitizer runs: Enter would otherwise stage — and, on
    // the show page, put in a URL — whatever was typed, card number included.
    event.target.blur();
    // add() closes the dialog itself once staging resolves. Closing here too would race the POST exactly
    // as the button's parallel close action did: the dialog vanishes, and a quick Save submits before the
    // hidden metric fields land, dropping the card while reporting success.
    this.add();
  }

  loadPreview() {
    if (!this.hasPreviewTarget) return;

    const params = new URLSearchParams({
      metric_key: this.metricTarget.value,
      dimension: this.selectedDimension(),
      ...this.presentOnly({
        series: this.selectedSeries(),
        companion: this.selectedCompanion(),
        limit: this.selectedLimit(),
        sort: this.hasSortTarget ? this.sortTarget.value : "",
        card_type: this.hasChartTypeTarget ? this.chartTypeTarget.value : "",
        rollup: this.hasRollupTarget && this.rollupTarget.checked && this.selectedLimit() !== "" ? "1" : "",
        target_lines: this.selectedTargetLines(),
      }),
    });
    // setAttribute rather than .src: the attribute is what Turbo observes to refetch, and it is set even
    // where the custom element's property accessor isn't defined.
    this.previewTarget.setAttribute("src", `${this.previewUrlValue}?${params}`);
  }

  // The fields this surface does not offer report "" rather than being absent, so they are dropped here
  // instead of travelling as empty values.
  presentOnly(fields) {
    return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== ""));
  }

  // Staging goes through the add-card controller on the surrounding scope, which owns the POST and the
  // turbo_stream append — the same path the section and chart items use. :show has no live grid, so it
  // navigates to the edit page with the metric staged instead.
  async add() {
    // Again-safe: the dialog closes on Add, but Enter and a double-click can both land before it does,
    // and each would stage its own card.
    if (this.staged) return;

    this.staged = true;
    const metricKey = this.metricTarget.value;
    const dimension = this.selectedDimension();
    // Blank so the server names it: the client's precomputed names are keyed on dimension|series only,
    // so a ranked chart would persist "Items Sold by Item" where the server writes "Top 8 items by ...".
    const name = this.nameIsGenerated ? "" : this.nameTarget.value.trim();

    // Blanks are dropped rather than sent empty: a control this surface never offered must not travel as
    // a choice the author made and left blank.
    const extra = this.presentOnly({
      subtitle: this.element.querySelector("[id$='live_metric_subtitle']")?.value.trim() || "",
      series: this.selectedSeries(),
      companion: this.selectedCompanion(),
      limit: this.selectedLimit(),
      sort: this.hasSortTarget ? this.sortTarget.value : "",
      card_type: this.hasChartTypeTarget ? this.chartTypeTarget.value : "",
      rollup: this.hasRollupTarget && this.rollupTarget.checked && this.selectedLimit() !== "" ? "1" : "",
      target_lines: this.selectedTargetLines(),
    });

    if (this.hasShowUrlTemplateValue && this.showUrlTemplateValue) {
      const filled = this.showUrlTemplateValue
        .replace("METRIC_KEY", encodeURIComponent(metricKey))
        .replace("DIMENSION", encodeURIComponent(dimension))
        .replace("NAME", encodeURIComponent(name));
      // Assigning the template untouched when there is nothing to add keeps the common path free of URL
      // parsing, and of the origin it would have to resolve against.
      const params = Object.entries(extra);
      if (params.length === 0) {
        window.location.assign(filled);
      } else {
        // Prefixed to match the show page's pending_* readers.
        const url = new URL(filled, window.location.origin);
        params.forEach(([key, value]) => url.searchParams.set(`pending_${key}`, value));
        window.location.assign(`${url.pathname}${url.search}`);
      }
    } else {
      // Awaited, and the dialog closes only afterwards: otherwise a quick Save submits before the hidden
      // metric fields land, reporting success while dropping the card.
      await this.addCardController().stage({ metric_key: metricKey, dimension, name, ...extra });
      this.element.querySelector("[data-ui--dialog-target='closeButton']")?.click();
    }
  }

  addCardController() {
    const scope = this.element.closest("[data-controller~='admin--report--add-card']");
    return this.application.getControllerForElementAndIdentifier(scope, "admin--report--add-card");
  }
}
